import type {
  Booking,
  CreateBookingDto,
  FindAllBookingsQueryDto,
  FindOneBookingQueryDto,
  UpdateBookingDto,
} from '@/entities/bookings/booking.entity';
import type { InvoicePositionDto } from '@/entities/invoices/invoice-position';
import type { PaginatedResponse } from '@/entities/pagination/paginated-response';
import { Service } from './http-service';
import type { ManyBookingsBooking } from '@/entities/bookings/many-bookings-booking.entity';
import type { PrivateFileKey } from '@/entities/private-file-key.interface';
import type {
  BookingJobDto,
  BookingsControllerGetCarSwapProtocolTemplateData,
  BookingsActionNeededCountsDto,
  CompleteHandoverDto,
  ManualLaneCreditDto,
  WriteOffAmountDto,
} from '@carvia/ros-client-types';
import type {
  CreateProcessSessionDto,
  PatchProcessSessionDto,
  ProcessSessionResponseDto,
} from '@carvia/ros-client-types';
import type { QueryLocale } from '@/entities/auth/local.enum';
import type {
  BookingsControllerSuggestCustomerDataResponse,
  RentalKeyTagLinkDto,
} from '@carvia/ros-client-types';
import type { LedgerEntry } from '@/entities/ledger/ledger-entry.entity';

class BookingsService extends Service<Booking, CreateBookingDto, UpdateBookingDto> {
  constructor() {
    super('/bookings');
  }

  async getAllWithQuery(params: FindAllBookingsQueryDto) {
    const res = await this.httpClient.get<PaginatedResponse<ManyBookingsBooking>>(
      `${this.endpoint}`,
      {
        params,
      },
    );
    return res.data;
  }

  async getOneWithQuery(id: string, query?: FindOneBookingQueryDto) {
    const res = await this.httpClient.get<Booking>(`${this.endpoint}/${id}`, { params: query });
    return res.data;
  }

  async countBookingsOfUser(userId: string) {
    const res = await this.httpClient.get<{
      completedBookingsCount: number;
      cancelledBookingsCount: number;
    }>(`${this.endpoint}/count-bookings-of-user/${userId}`);
    return res.data;
  }

  async getActionNeededCounts(params?: {
    stationId?: string;
    query?: string;
    bookingNumber?: number;
  }) {
    const res = await this.httpClient.get<BookingsActionNeededCountsDto>(
      `${this.endpoint}/action-needed-counts`,
      {
        params: {
          stationId: params?.stationId,
          query: params?.query?.trim() || undefined,
          bookingNumber: params?.bookingNumber,
        },
      },
    );
    return res.data;
  }

  async getAllInvoicePositions(bookingId: string) {
    const res = await this.httpClient.get<InvoicePositionDto[]>(
      `${this.endpoint}/${bookingId}/invoice-positions`,
    );
    return res.data;
  }

  async getBookingContractTemplate(id: string, lang: QueryLocale) {
    const res = await this.httpClient.get<string>(`${this.endpoint}/${id}/contract-template`, {
      responseType: 'text',
      params: {
        lang,
      },
    });
    return res.data;
  }

  async getCarSwapProtocolTemplate(
    bookingId: string,
    query: BookingsControllerGetCarSwapProtocolTemplateData['query'] & { lang: QueryLocale },
  ) {
    const res = await this.httpClient.get<string>(
      `${this.endpoint}/${bookingId}/car-swap-protocol-template`,
      {
        responseType: 'text',
        params: query,
      },
    );
    return res.data;
  }

  async ensureCustomerLinked(bookingId: string) {
    const res = await this.httpClient.post<Booking>(
      `${this.endpoint}/${bookingId}/ensure-customer-linked`,
    );
    return res.data;
  }

  async completeHandover(bookingId: string, data: CompleteHandoverDto) {
    const res = await this.httpClient.post<Booking>(
      `${this.endpoint}/${bookingId}/complete-handover`,
      data,
    );
    return res.data;
  }

  async resendContract(bookingId: string) {
    const res = await this.httpClient.post<string>(
      `${this.endpoint}/${bookingId}/mails/handed-over`,
    );
    return res.data;
  }

  async resendUpdatedContractImmediately(bookingId: string) {
    const res = await this.httpClient.post<string>(
      `${this.endpoint}/${bookingId}/mails/updated-contract`,
    );
    return res.data;
  }

  async resendBookingConfirmation(bookingId: string) {
    const res = await this.httpClient.post<string>(
      `${this.endpoint}/${bookingId}/mails/booking-confirmation`,
    );
    return res.data;
  }

  async sendDepositReceipt(bookingId: string, receiptText: string) {
    const res = await this.httpClient.post<string>(
      `${this.endpoint}/${bookingId}/mails/deposit-receipt`,
      { receiptText },
    );
    return res.data;
  }

  async sendRentalKeyLink(bookingId: string, sendMail: boolean) {
    const res = await this.httpClient.post<RentalKeyTagLinkDto>('/rental-key-tag/send-link', {
      bookingId,
      sendMail,
    });
    return res.data;
  }

  async refreshBookingConfirmation(bookingId: string) {
    const res = await this.httpClient.post<PrivateFileKey>(
      `${this.endpoint}/${bookingId}/refresh-booking-confirmation`,
    );
    return res.data;
  }

  async getJobs(bookingId: string) {
    const res = await this.httpClient.get<BookingJobDto[]>(`${this.endpoint}/${bookingId}/jobs`);
    return res.data;
  }

  async suggestCustomerData(q: string) {
    const res = await this.httpClient.get<BookingsControllerSuggestCustomerDataResponse>(
      `${this.endpoint}/suggest-customer-data`,
      { params: { q } },
    );
    return res.data;
  }

  async getProcessSession(bookingId: string) {
    const res = await this.httpClient.get<ProcessSessionResponseDto | null>(
      `${this.endpoint}/${bookingId}/process-session`,
    );
    return res.data;
  }

  async createProcessSession(bookingId: string, dto: CreateProcessSessionDto) {
    const res = await this.httpClient.post<ProcessSessionResponseDto>(
      `${this.endpoint}/${bookingId}/process-session`,
      dto,
    );
    return res.data;
  }

  async patchProcessSession(bookingId: string, dto: PatchProcessSessionDto) {
    const res = await this.httpClient.patch<ProcessSessionResponseDto>(
      `${this.endpoint}/${bookingId}/process-session`,
      dto,
    );
    return res.data;
  }

  async deleteProcessSession(bookingId: string) {
    await this.httpClient.delete(`${this.endpoint}/${bookingId}/process-session`);
  }

  async convertToLongTerm(
    bookingId: string,
    dto: {
      bookedExtras: { vehicleExtraId?: string; vehicleExtraOptionId?: string }[];
      customBookedExtras?: unknown;
      customTotalBasePrice: number;
      quoteStartDate: string;
      invoicePickupTransfer?: boolean;
      invoiceDropoffTransfer?: boolean;
    },
  ) {
    const res = await this.httpClient.post<Booking>(
      `${this.endpoint}/${bookingId}/convert-to-long-term`,
      dto,
    );
    return res.data;
  }

  async writeOffAmount(bookingId: string, dto: WriteOffAmountDto) {
    const res = await this.httpClient.post<LedgerEntry>(
      `${this.endpoint}/${bookingId}/write-off`,
      dto,
    );
    return res.data;
  }

  async manualLaneCredit(bookingId: string, dto: ManualLaneCreditDto) {
    const res = await this.httpClient.post<LedgerEntry[]>(
      `${this.endpoint}/${bookingId}/manual-lane-credits`,
      dto,
    );
    return res.data;
  }
}

export const bookingsService = new BookingsService();
